import {
  computeProgress,
  eventOccurrences,
  mergeOccurrences,
  type AllDayEvent,
  DEFAULT_ZONE,
  expandOccurrences,
  groupByDate,
  startOfWeek,
  addDays,
  todayIn,
  type LocalDate,
  type Occurrence,
  type Progress,
  EMPTY_PLAN,
  withPlanDefaults,
  MAX_MOVE_DAYS,
  resolveDay,
  allDayOn,
  sleepFor,
  type AllDayItem,
  type ResolvedDay,
  type ResolvedSleep,
} from '@planer/shared';
import { itemColor } from './colors';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { api, type Plan } from './api';
import { buildStatusLookup, type StatusLookup } from './checkinState';
import { useOutbox } from './outbox';

export const queryKeys = {
  session: ['session'] as const,
  settings: ['settings'] as const,
  schedule: ['schedule'] as const,
  checkins: (from: string, to: string) => ['checkins', from, to] as const,
  events: (from: string, to: string) => ['events', from, to] as const,
  calendars: ['calendars'] as const,
  plan: ['plan'] as const,
  streaks: ['streaks'] as const,
};

/** Days in a row per habit / reminder (item id → n; only 2 and more). */
export function useStreaks(): Record<string, number> {
  const q = useQuery({ queryKey: queryKeys.streaks, queryFn: api.streaks, staleTime: 5 * 60_000 });
  return q.data?.streaks ?? {};
}

export function usePlan() {
  return useQuery({
    queryKey: queryKeys.plan,
    queryFn: api.plan,
    // Tolerates an older server or data cached before an update (missing lists/settings).
    select: (data: Partial<Plan>): Plan => ({
      ...withPlanDefaults(data),
      goals: data.goals ?? [],
      tasks: data.tasks ?? [],
    }),
  });
}

export function useNow(intervalMs = 30_000): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = window.setInterval(() => {
      setNow(new Date());
    }, intervalMs);
    const onVisible = () => {
      if (document.visibilityState === 'visible') setNow(new Date());
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [intervalMs]);
  return now;
}

const subscribeOnline = (fn: () => void) => {
  window.addEventListener('online', fn);
  window.addEventListener('offline', fn);
  return () => {
    window.removeEventListener('online', fn);
    window.removeEventListener('offline', fn);
  };
};
export const useOnline = () => useSyncExternalStore(subscribeOnline, () => navigator.onLine);

export function useZone(): string {
  const { data } = useQuery({ queryKey: queryKeys.settings, queryFn: api.settings });
  return data?.timezone ?? DEFAULT_ZONE;
}

export function useToday(): LocalDate {
  const zone = useZone();
  const now = useNow();
  return todayIn(zone, now);
}

export function useSchedule() {
  return useQuery({ queryKey: queryKeys.schedule, queryFn: api.schedule });
}

export interface AllDayEntry {
  key: string;
  title: string;
  /** CSS colour. */
  color: string;
  /** Set for own events (editable); calendar events carry the calendar name. */
  own: AllDayItem | null;
  calendar?: string;
}

export interface WeekData {
  days: LocalDate[];
  byDate: Map<LocalDate, Occurrence[]>;
  /** Whole day (for the day ring). */
  progress: Map<LocalDate, Progress>;
  /** Only what is already due: past days, today's finished or already marked items. */
  soFar: Map<LocalDate, Progress>;
  /** All-day calendar events per date (shown above the timeline, not counted). */
  allDayByDate: Map<LocalDate, AllDayEvent[]>;
  weekProgress: Progress;
  /** Which day template applies on each day (and why; `pause` = holiday). */
  dayInfo: Map<LocalDate, ResolvedDay>;
  /** Wake-up and bedtime of each day. */
  sleep: Map<LocalDate, ResolvedSleep>;
  /** Own all-day events and all-day calendar events, own first. */
  allDay: Map<LocalDate, AllDayEntry[]>;
  status: StatusLookup;
  isLoading: boolean;
  hasItems: boolean;
}

/** Schedule + check-ins for the ISO week containing `date`, all computed on the device. */
export function useWeek(date: LocalDate, today: LocalDate): WeekData {
  const zone = useZone();
  const schedule = useSchedule();
  const plan = usePlan();
  const from = startOfWeek(date);
  const to = addDays(from, 6);
  // Occurrences can be moved in from nearby days; their check-ins live on the original date.
  const checkFrom = addDays(from, -MAX_MOVE_DAYS);
  const checkTo = addDays(to, MAX_MOVE_DAYS);
  const checkins = useQuery({
    queryKey: queryKeys.checkins(checkFrom, checkTo),
    queryFn: () => api.checkins(checkFrom, checkTo),
  });
  const events = useQuery({
    queryKey: queryKeys.events(from, to),
    queryFn: () => api.events(from, to),
    // Calendars sync on the server every few minutes; refresh in the background.
    refetchInterval: 5 * 60_000,
  });
  const pending = useOutbox();
  const now = useNow(60_000);
  const nowIso = now.toISOString();

  return useMemo(() => {
    const days = Array.from({ length: 7 }, (_, i) => addDays(from, i));
    const planContext = plan.data ?? EMPTY_PLAN;
    const occurrences = schedule.data
      ? expandOccurrences({
          items: schedule.data.items,
          exceptions: schedule.data.exceptions,
          from,
          to,
          zone,
          plan: planContext,
        })
      : [];
    const imported = eventOccurrences({ events: events.data?.events ?? [], from, to, zone });
    const occurrencesAll = mergeOccurrences(occurrences, imported.timed);
    const byDate = groupByDate(occurrencesAll);
    const allDayByDate = new Map<LocalDate, AllDayEvent[]>();
    for (const a of imported.allDay) {
      allDayByDate.set(a.date, [...(allDayByDate.get(a.date) ?? []), a]);
    }
    const status = buildStatusLookup(checkins.data?.checkins, pending);
    const progress = new Map(days.map((d) => [d, computeProgress(byDate.get(d) ?? [], status)]));
    // Something that has not happened yet is not a failure: count only what is due.
    const due = (o: Occurrence) =>
      o.date < today || (o.date === today && (o.end <= nowIso || status(o.key) != null));
    const soFar = new Map(
      days.map((d) => [d, computeProgress((byDate.get(d) ?? []).filter(due), status)]),
    );
    return {
      days,
      byDate,
      progress,
      soFar,
      allDayByDate,
      weekProgress: computeProgress(occurrencesAll.filter(due), status),
      dayInfo: new Map(days.map((d) => [d, resolveDay(planContext, d)])),
      sleep: new Map(days.map((d) => [d, sleepFor(planContext, d)])),
      allDay: new Map(
        days.map((d) => [
          d,
          [
            ...allDayOn(planContext, d).map((e): AllDayEntry => ({
              key: e.id,
              title: e.title,
              color: itemColor(e.color),
              own: e,
            })),
            ...(allDayByDate.get(d) ?? []).map((a): AllDayEntry => ({
              key: a.key,
              title: a.title,
              color: a.calendar.color,
              own: null,
              calendar: a.calendar.name,
            })),
          ],
        ]),
      ),
      status,
      isLoading: schedule.isPending,
      hasItems: (schedule.data?.items.length ?? 0) > 0 || imported.timed.length > 0,
    };
  }, [
    schedule.data,
    schedule.isPending,
    plan.data,
    checkins.data,
    events.data,
    pending,
    from,
    to,
    zone,
    today,
    nowIso,
  ]);
}
